'use client';

import * as React from 'react';
import clsx from 'clsx';
import { Command as CommandPrimitive } from 'cmdk';
import { IconXClose } from 'icon/General/x-close';
import { Badge } from './badge';
import { Command, CommandGroup, CommandItem, CommandList } from './command';

type MultiOption = {
  label: React.ReactNode;
  value: string;
};

export type MultiSelectProps = {
  options: MultiOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  id?: string;
};

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select options...',
  id,
}: MultiSelectProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const handleUnselect = (option: MultiOption) => {
    const newSelected = selected.filter((s) => s !== option.value);
    onChange(newSelected);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          const newSelected = [...selected];
          newSelected.pop();
          onChange(newSelected);
        }
      }
      // This is not a default behaviour of the <input /> field
      if (e.key === 'Escape') {
        input.blur();
      }
    }
  };

  // console.log(selectables, selected, inputValue);
  const selectedOptions = selected
    .map((s) => options.find((o) => o.value === s))
    .filter((o) => !!o);
  const selectables = options.filter(
    (option) => !selected.includes(option.value),
  );
  return (
    <Command
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
    >
      <div
        className={clsx(
          'group rounded-md border px-3 py-2 text-sm',
          'focus-within:border-brand-300 focus-within:shadow-[0_0_0_4px_rgb(var(--brand-500)/24%),0_1px_2px_0_rgb(var(--gray-900)/6%),0_1px_3px_0_rgb(var(--gray-900)/10%)] focus-within:outline-hidden',
        )}
      >
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((option) => {
            return (
              <Badge key={option.value} variant="secondary">
                {option.label}
                <button
                  type="button"
                  className="ml-1 rounded-full p-0.5 outline-hidden ring-brand-200 hover:bg-gray-300 focus:ring-2 focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnselect(option);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(option)}
                >
                  <IconXClose className="size-3 text-gray-500 hover:text-gray-600" />
                </button>
              </Badge>
            );
          })}
          {/* Avoid having the "Search" Icon */}
          {selectables.length > 0 && (
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="ml-2 flex-1 bg-transparent outline-hidden placeholder:text-gray-400"
              id={id}
            />
          )}
        </div>
      </div>
      <div className="relative mt-2">
        <CommandList>
          {open && selectables.length > 0 ? (
            <div className="absolute top-0 z-10 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-md border bg-white text-gray-900 shadow-md outline-hidden animate-in">
              <CommandGroup className="h-full overflow-auto">
                {selectables.map((option) => {
                  return (
                    <CommandItem
                      key={option.value}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onSelect={() => {
                        setInputValue('');
                        const newSelected = [...selected, option.value];
                        onChange(newSelected);
                      }}
                      className="cursor-pointer"
                    >
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  );
}
